import { useEffect, type ReactNode } from "react";

import { authClient } from "@/lib/auth-client";
import { identifyUser, initPosthog } from "@/lib/posthog";
import { initMetaPixel } from "@/lib/metapixel";
import { captureAdClickFromUrl, readMetaIdentifiers } from "@/lib/attribution";
import fetchToBackend from "@/utils/fetchToBackend";

// Guarda a qué usuario ya se le mandó la atribución, para no repetir la llamada
// en cada carga. Se escribe solo si el POST salió bien: un fallo reintenta.
const SENT_KEY = "licitigo.attribution.sent";

/**
 * Arranca la analítica, le pone nombre a la sesión y persiste la atribución del
 * anuncio. Va por fuera del router: los pageviews se capturan solos desde la
 * History API, así que aquí no hace falta saber en qué ruta estamos.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { data: session } = authClient.useSession();
  const user = session?.user;

  useEffect(() => {
    // El `fbclid` se guarda antes que nada y en toda ruta: llega en la primera
    // visita, que suele ser el registro, mucho antes de que exista una sesión.
    captureAdClickFromUrl();
    initPosthog();
    initMetaPixel();
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    identifyUser(user.id, { email: user.email, name: user.name });
  }, [user?.id, user?.email, user?.name]);

  // Recién con sesión se puede guardar la atribución en el servidor, que es donde
  // tiene que estar cuando la compra ocurra días después.
  useEffect(() => {
    if (!user?.id) return;
    void persistAttribution(user.id);
  }, [user?.id]);

  return <>{children}</>;
}

async function persistAttribution(userId: string): Promise<void> {
  try {
    if (localStorage.getItem(SENT_KEY) === userId) return;

    await fetchToBackend("/userextrainformation/attribution", {
      method: "POST",
      body: JSON.stringify(readMetaIdentifiers()),
    });
    localStorage.setItem(SENT_KEY, userId);
  } catch {
    // Nunca rompe la app: sin atribución se pierde la conversión, no la sesión.
  }
}
